import { useState, useEffect } from 'react';
import api from '../../services/api';
import { fetchPromotores } from '../../services/promotores';
import { useAuth } from '../../contexts/AuthContext';

function AtendimentoPromotoriaModal({
  isOpen,
  onClose,
  atendimento,
  movimentacaoId,
  movimentacaoFinalizada,
  onSaveSuccess,
  promotores = [],
  adolescenteNome,
  theme = 'dark',
}) {
  const { hasAnyPermission } = useAuth();

  const canEdit = hasAnyPermission(['atendimentos.create', 'atendimentos.edit']) && !movimentacaoFinalizada;
  const isViewOnly = !canEdit && hasAnyPermission(['atendimentos.view']);

  // Lista local de promotores (usada quando o pai não passa nada — ex.: Dashboard)
  const [localPromotores, setLocalPromotores] = useState([]);
  const [promotoresLoaded, setPromotoresLoaded] = useState(false);

  const [formData, setFormData] = useState({
    promotor_id: '',
    horario_atendimento: '',
    observacao: '',
    check_entrada: false,
    check_saida: false,
    check_online: false,
  });
  
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  // Fonte final de promotores: prioridade para o que vem do pai,
  // se vier vazio, usamos a lista carregada internamente.
  const promotoresOptionsRaw = promotores.length > 0 ? promotores : localPromotores;
  const promotoresOptions = [...promotoresOptionsRaw].sort((a, b) =>
    (a?.nome || '').localeCompare((b?.nome || ''), 'pt-BR', { sensitivity: 'base' })
  );
  const showPromotoresLoading =
    canEdit && !promotoresLoaded && promotoresOptions.length === 0;

  useEffect(() => {
    if (atendimento) {
      setFormData({
        promotor_id: atendimento.promotor_id || '',
        horario_atendimento: atendimento.horario_atendimento || '',
        observacao: atendimento.observacao || '',
        check_entrada: !!atendimento.check_entrada,
        check_saida: !!atendimento.check_saida,
        check_online: !!atendimento.check_online,
      });
    } else if (isOpen) {
      setFormData({
        promotor_id: '',
        horario_atendimento: '',
        observacao: '',
        check_entrada: false,
        check_saida: false,
        check_online: false,
      });
    }
  }, [atendimento, isOpen]);

  // Carrega Promotores localmente quando a modal abre e o pai não passou lista (ex.: Dashboard)
  useEffect(() => {
    if (!isOpen) {
      setPromotoresLoaded(false);
      return;
    }

    if (promotores.length > 0 || localPromotores.length > 0) {
      setPromotoresLoaded(true);
    }
  }, [isOpen, promotores.length, localPromotores.length]);

  useEffect(() => {
    if (!isOpen || !canEdit) return;
    if (promotores.length > 0 || localPromotores.length > 0) return;

    setPromotoresLoaded(false);
    fetchPromotores()
      .then(setLocalPromotores)
      .catch(() => {
        setLocalPromotores([]);
      })
      .finally(() => {
        setPromotoresLoaded(true);
      });
  }, [isOpen, canEdit, promotores.length, localPromotores.length]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    if (isViewOnly) return; // segurança extra no front

    const { name, value, type, checked } = e.target;
    const val = type === 'checkbox' ? checked : value;

    setFormData((prev) => {
      const next = { ...prev, [name]: val };
      // Regra: desmarcar "Finalizado" se "Iniciado" for desmarcado
      if (name === 'check_entrada' && !val) next.check_saida = false;
      return next;
    });

    if (validationErrors[name]) {
      setValidationErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canEdit) return;

    setSaving(true);
    setError(null);
    setValidationErrors({});

    const payload = {
      ...formData,
      promotor_id: formData.promotor_id || null,
      horario_atendimento: formData.horario_atendimento || null,
    };

    try {
      if (atendimento) {
        await api.put(`/atendimentos-promotoria/${atendimento.id}`, payload);
      } else {
        await api.post(`/movimentacoes/${movimentacaoId}/atendimentos-promotoria`, payload);
      }
      onSaveSuccess && onSaveSuccess('Atendimento da Promotoria salvo com sucesso!');
      onClose();
    } catch (err) {
      const msg =
        err.userMessage ||
        err.response?.data?.message ||
        'Ocorreu um erro ao salvar o atendimento.';
      setError(msg);
      if (err.validationErrors) setValidationErrors(err.validationErrors);
      if (!err.silence) console.error('Erro ao salvar atendimento da Promotoria:', err);
    } finally {
      setSaving(false);
    }
  };

  const getPromotorNome = () => {
    // Prioridade 1: Nome que vem do backend (funciona mesmo com promotor excluído)
    if (atendimento?.promotor_nome) {
      return atendimento.promotor_nome;
    }
    
    // Prioridade 2: Busca na lista de promotores ativos
    if (!formData.promotor_id) return '—';
    const prom = promotoresOptions.find((p) => p.id === Number(formData.promotor_id));
    return prom?.nome || '—';
  };

  const disableFields = isViewOnly || saving;

  return (
    <div
      className={`modal fade show dashboard-themed-modal dashboard-themed-modal--${theme}`}
      style={{ display: 'block' }}
      tabIndex="-1"
      role="dialog"
      aria-modal="true"
    >
      <div className="modal-dialog modal-dialog-centered" role="document">
        <div className="modal-content bg-dark text-light dashboard-themed-modal-content">
          {/* HEADER */}
          <div className="modal-header border-0 pb-1">
            <h5 className="modal-title atendimento-modal-title">Atendimento da Promotoria</h5>
            <button
              type="button"
              className={theme === 'light' ? 'btn-close' : 'btn-close btn-close-white'}
              onClick={onClose}
              disabled={saving}
              aria-label="Fechar"
            ></button>
          </div>

          {/* HR + NOME DO ADOLESCENTE (fora do header, em uma seção própria) */}
          <div className="px-4">
            <hr className="mt-0 mb-2 border-secondary border-opacity-50" />
            {adolescenteNome && (
              <div className="mb-2">
                <span className="text-secondary me-1">Adolescente:</span>
                <span className="fw-semibold text-light">{adolescenteNome}</span>
              </div>
            )}
          </div>

          {/* BODY */}
          <div className="modal-body pt-2">
            {error && <div className="alert alert-danger">{error}</div>}

            {/* MODO VISUALIZAÇÃO: mostra dados em "read-only bonito" */}
            {isViewOnly ? (
              <div className="mb-3">
                <div className="mb-2">
                  <small className="text-secondary d-block">Nome do Promotor</small>
                  <span className="text-light">
                    {getPromotorNome()}
                  </span>
                </div>

                <div className="mb-2">
                  <small className="text-secondary d-block">Horário do atendimento</small>
                  <span className="text-light">
                    {formData.horario_atendimento || '—'}
                  </span>
                </div>

                <div className="mb-2">
                  <small className="text-secondary d-block">Observação</small>
                  <span className="text-light">
                    {formData.observacao || '—'}
                  </span>
                </div>

                <div className="mt-3">
                  <small className="text-secondary d-block">Status</small>
                  <span className="text-light">
                    {formData.check_entrada
                      ? formData.check_saida
                        ? 'Finalizado'
                        : 'Iniciado'
                      : 'Não iniciado'}
                  </span>
                </div>

                <div className="mt-2">
                  <small className="text-secondary d-block">Modalidade</small>
                  <span className="text-light">
                    {formData.check_online ? (
                      <span style={{ color: '#2f7a4b', fontWeight: 600 }}>Online</span>
                    ) : 'Presencial'}
                  </span>
                </div>
              </div>
            ) : (
              // MODO EDIÇÃO: formulário normal
              <form onSubmit={handleSubmit} noValidate>
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <label htmlFor="promotor_id" className="form-label mb-0">
                      Nome do Promotor <span className="text-danger">*</span>
                    </label>
                    <div className="form-check form-switch mb-0 d-flex align-items-center gap-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="check_online_promotoria"
                        name="check_online"
                        checked={formData.check_online}
                        onChange={handleChange}
                        disabled={disableFields}
                        style={{ cursor: disableFields ? 'not-allowed' : 'pointer' }}
                      />
                      <label
                        className="form-check-label"
                        htmlFor="check_online_promotoria"
                        style={{
                          cursor: disableFields ? 'not-allowed' : 'pointer',
                          color: formData.check_online ? '#2f7a4b' : 'inherit',
                          fontWeight: formData.check_online ? 600 : 'inherit',
                        }}
                      >
                        Online
                      </label>
                    </div>
                  </div>
                  <select
                    id="promotor_id"
                    name="promotor_id"
                    className={`form-select ${
                      validationErrors.promotor_id ? 'is-invalid' : ''
                    }`}
                    value={formData.promotor_id}
                    onChange={handleChange}
                    disabled={disableFields || showPromotoresLoading}
                  >
                    {showPromotoresLoading ? (
                      <option value="">Carregando...</option>
                    ) : (
                      <>
                        <option value="">Selecione...</option>
                        {promotoresOptions.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.nome}
                          </option>
                        ))}
                      </>
                    )}
                  </select>
                  {validationErrors.promotor_id && (
                    <div className="invalid-feedback">
                      {validationErrors.promotor_id.join(', ')}
                    </div>
                  )}
                </div>

                <div className="mb-3">
                  <label htmlFor="horario_atendimento" className="form-label">
                    Horário de atendimento
                  </label>
                  <input
                    type="time"
                    id="horario_atendimento"
                    name="horario_atendimento"
                    className={`form-control ${
                      validationErrors.horario_atendimento ? 'is-invalid' : ''
                    }`}
                    value={formData.horario_atendimento}
                    onChange={handleChange}
                    disabled={disableFields}
                    step="60"
                  />
                  {validationErrors.horario_atendimento && (
                    <div className="invalid-feedback">
                      {validationErrors.horario_atendimento.join(', ')}
                    </div>
                  )}
                </div>

                <div className="mb-3">
                  <label htmlFor="observacao" className="form-label">
                    Observação
                  </label>
                  <textarea
                    className={`form-control ${
                      validationErrors.observacao ? 'is-invalid' : ''
                    }`}
                    id="observacao"
                    name="observacao"
                    rows="3"
                    value={formData.observacao}
                    onChange={handleChange}
                    disabled={disableFields}
                  ></textarea>
                  {validationErrors.observacao && (
                    <div className="invalid-feedback">
                      {validationErrors.observacao.join(', ')}
                    </div>
                  )}
                </div>

                <div className="form-check form-switch mb-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="check_entrada_promotoria"
                    name="check_entrada"
                    checked={formData.check_entrada}
                    onChange={handleChange}
                    disabled={disableFields}
                  />
                  <label
                    className="form-check-label"
                    htmlFor="check_entrada_promotoria"
                  >
                    Atendimento Iniciado
                  </label>
                </div>

                <div className="form-check form-switch mb-1">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="check_saida_promotoria"
                    name="check_saida"
                    checked={formData.check_saida}
                    onChange={handleChange}
                    disabled={disableFields || !formData.check_entrada}
                  />
                  <label
                    className="form-check-label"
                    htmlFor="check_saida_promotoria"
                  >
                    Atendimento Finalizado
                  </label>
                </div>

                {!isViewOnly && (
                  <small className="text-secondary">
                    Para finalizar, o atendimento deve estar "Iniciado".
                  </small>
                )}
              </form>
            )}
          </div>

          {/* FOOTER */}
          <div className="modal-footer">
            {isViewOnly ? (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={saving}
              >
                Fechar
              </button>
            ) : (
              <>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={onClose}
                  disabled={saving}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  onClick={handleSubmit}
                  disabled={saving}
                >
                  {saving ? 'Salvando...' : 'Salvar'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AtendimentoPromotoriaModal;
